import React from "react";
import { Loader } from "semantic-ui-react";

import Icon from "../Icon";
import { ComponentPassThrough } from "../types";
import {
  DoneWrapper,
  LoadingWrapper,
  StyledAnchor,
  StyledExternalAnchor,
  StyledButton,
} from "./styled";
import { ButtonVariants, ButtonSizes } from "./types";

export interface NextButtonProps {
  to?: string;
  replace?: boolean;
  href?: string;
  target?: string;
  variant: ButtonVariants;
  underlined?: boolean;
  size?: ButtonSizes;
  fullWidth?: boolean;
  iconOnly?: boolean;
  compact?: boolean;
  margin?: React.CSSProperties["margin"];
  height?: React.CSSProperties["height"];
  rounded?: boolean;
  isLoading?: boolean;
  isDone?: boolean;
  disabled?: boolean;
}

function NextButton<T extends React.ElementType = "button" | "a">({
  to,
  href,
  variant,
  size = "l",
  fullWidth = false,
  iconOnly = false,
  compact = false,
  margin,
  children,
  height,
  rounded = false,
  replace = false,
  isLoading = false,
  isDone = false,
  underlined,
  ...props
}: ComponentPassThrough<T, NextButtonProps>) {
  const sharedProps = {
    ...props,
    $underlined: underlined,
    $size: size,
    $variant: variant,
    $fullWidth: fullWidth,
    $iconOnly: iconOnly,
    $compact: compact,
    $margin: margin,
    $height: height,
    $rounded: rounded,
    children,
    disabled: props.disabled ?? true,
  };
  if (to) {
    return <StyledAnchor {...sharedProps} href={to} replace={replace} />;
  } else if (href) {
    return <StyledExternalAnchor {...sharedProps} href={href} />;
  }

  const isDark = [
    "primary",
    "blue",
    "white-blue",
    "purple-blue",
    "danger",
  ].includes(variant);
  const isBig = ["xl", "l"].includes(size);

  return (
    <StyledButton {...sharedProps} data-loading={isLoading} data-done={isDone}>
      <LoadingWrapper data-ui>
        <Loader
          active
          inline
          size={isBig ? "small" : "tiny"}
          inverted={isDark}
        />
      </LoadingWrapper>
      <DoneWrapper data-ui>
        <Icon
          name="check"
          color={isDark ? "white" : "primary"}
          size={isBig ? "26px" : "18px"}
        />
      </DoneWrapper>
      {children}
    </StyledButton>
  );
}

export default NextButton;
